import React, { useState, useEffect } from 'react';
import axiosInstance from '../axiosConfig';
import { formatValue } from '../utils/formatValue';
import { handleError } from '../services/handleError';
import Loading from "./Loading";
import { ShoppingCart, ChefHat, DollarSign, AlertTriangle, TrendingUp, Receipt } from 'lucide-react';

function Dashboard() {
    const [pedidos, setPedidos] = useState(0);
    const [ranking, setRanking] = useState([]);
    const [faturamento, setFaturamento] = useState(0);
    const [insumosAlerta, setInsumosAlerta] = useState([]);
    const [ticketMedio, setTicketMedio] = useState(0);
    const [crescimentoPedidos, setCrescimentoPedidos] = useState([]);
    const [filtroPeriodo, setFiltroPeriodo] = useState('dia');
    const [loading, setLoading] = useState(true);
    const [dataInicial, setDataInicial] = useState("");
    const [dataFinal, setDataFinal] = useState("");

    useEffect(() => {
        if (filtroPeriodo !== "personalizado") {
            const { dataInicial, dataFinal } = calcularIntervalo(filtroPeriodo);
            setDataInicial(dataInicial);
            setDataFinal(dataFinal);
        }
    }, [filtroPeriodo]);

    useEffect(() => {
        if (!dataInicial || !dataFinal) return;

        async function buscarDados() {
            setLoading(true);
            try {
                let url = "";
                if (filtroPeriodo === "personalizado") {
                    url = `Relatorios/Dashboard?dataInicial=${dataInicial}&dataFinal=${dataFinal}`;
                } else {
                    url = `Relatorios/Dashboard?periodo=${filtroPeriodo}`;
                }

                const response = await axiosInstance.get(url);
                setPedidos(response.data.totalPedidos);
                setRanking(response.data.rankingPratos);
                setFaturamento(response.data.totalFaturamento);
                setInsumosAlerta(response.data.insumosEmAlerta);
                setTicketMedio(response.data.ticketMedio);
                setCrescimentoPedidos(response.data.crescimentoMensalPedidos);
            } catch (error) {
                handleError(error, "Falha ao carregar os dados.");
            } finally {
                setLoading(false);
            }
        }

        buscarDados();
    }, [dataInicial, dataFinal]);

    const handlePeriodoChange = (e) => {
        setFiltroPeriodo(e.target.value);
    };

    function calcularIntervalo(periodo) {
        const hoje = new Date();
        let inicio, fim;

        switch (periodo) {
            case "dia":
                inicio = fim = hoje;
                break;
            case "mes":
                inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
                fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
                break;
            case "ano":
                inicio = new Date(hoje.getFullYear(), 0, 1);
                fim = new Date(hoje.getFullYear(), 11, 31);
                break;
            default:
                inicio = fim = hoje;
        }

        return {
            dataInicial: inicio.toISOString().slice(0, 10),
            dataFinal: fim.toISOString().slice(0, 10),
        };
    }

    if (loading) return <Loading />;

    return (
        <div className="min-h-screen bg-gray-50 py-10 px-6 flex flex-col items-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-8">Dashboard</h1>

            <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
                <div className="flex items-center gap-3">
                    <label htmlFor="filtro-periodo" className="font-medium text-zinc-600">
                        Período:
                    </label>

                    <div className="relative">
                        <select
                            id="filtro-periodo"
                            className="appearance-none pl-3 pr-8 py-2 text-sm border border-zinc-300 rounded-lg shadow-sm bg-white text-zinc-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            value={filtroPeriodo}
                            onChange={handlePeriodoChange}
                        >
                            <option value="dia">Dia</option>
                            <option value="mes">Mês</option>
                            <option value="ano">Ano</option>
                            <option value="personalizado">Personalizado</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-zinc-400">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>
                </div>

                {filtroPeriodo === "personalizado" && (
                    <div className="flex flex-wrap items-center gap-2">
                        <input
                            type="date"
                            className="w-[130px] px-3 py-2 text-sm border border-zinc-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            value={dataInicial}
                            onChange={(e) => setDataInicial(e.target.value)}
                        />
                        <span className="text-zinc-500">até</span>
                        <input
                            type="date"
                            className="w-[130px] px-3 py-2 text-sm border border-zinc-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            value={dataFinal}
                            onChange={(e) => setDataFinal(e.target.value)}
                        />
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-6xl">
                {/* Pedidos */}
                <div className="bg-white rounded-2xl shadow p-6 flex flex-col items-center text-center transition hover:shadow-lg">
                    <ShoppingCart className="text-indigo-500 mb-3" size={32} />
                    <h2 className="text-lg font-semibold text-gray-700">Pedidos Finalizados</h2>
                    <p className="text-4xl font-bold text-indigo-600 mt-2">{pedidos}</p>
                </div>

                {/* Ranking */}
                <div className="bg-white rounded-2xl shadow p-6 flex flex-col items-center text-center transition hover:shadow-lg">
                    <ChefHat className="text-yellow-500 mb-3" size={32} />
                    <h2 className="text-lg font-semibold text-gray-700">Top 3 Produtos</h2>
                    <ol className="mt-4 text-gray-600 space-y-1 text-sm">
                        {ranking.length === 0 ? (
                            <li className="italic text-gray-400">Sem pedidos no período</li>
                        ) : (
                            ranking.map((prato, index) => (
                                <li key={index}>
                                    <span className="font-medium">{prato.totalVendido}x</span> {prato.prato}
                                </li>
                            ))
                        )}
                    </ol>
                </div>

                {/* Faturamento */}
                <div className="bg-white rounded-2xl shadow p-6 flex flex-col items-center text-center transition hover:shadow-lg">
                    <DollarSign className="text-green-500 mb-3" size={32} />
                    <h2 className="text-lg font-semibold text-gray-700">Faturamento</h2>
                    <p className="text-4xl font-bold text-green-600 mt-2">{formatValue(faturamento)}</p>
                </div>

                {/* Itens em alerta de estoque */}
                <div className="bg-white rounded-2xl shadow p-6 flex flex-col items-center text-center transition hover:shadow-lg">
                    <AlertTriangle className="text-red-500 mb-3" size={32} />
                    <h2 className="text-lg font-semibold text-red-600">{insumosAlerta.length} Produtos em Alerta</h2>

                    <ul className="mt-4 text-gray-600 text-sm space-y-1 max-h-40 overflow-auto">
                        {insumosAlerta.length === 0 ? (
                            <p className="text-gray-500 text-sm mt-4">Nenhum insumo em alerta no momento.</p>
                        ) :
                            insumosAlerta.map((insumo) => (
                                <li key={insumo.id || insumo.nome}>
                                    <span className="font-medium text-red-700">{insumo.nome}</span> – {insumo.quantidadeEstoque} {insumo.unidade} (mín. {insumo.quantidadeMinima})
                                </li>
                            ))}
                    </ul>
                </div>

                {/* Ticket médio */}
                <div
                    className="bg-white rounded-2xl shadow p-6 flex flex-col items-center text-center transition-shadow hover:shadow-lg"
                    aria-label="Ticket médio do último mês"
                >
                    <Receipt className="text-green-500 mb-3" size={32} aria-hidden="true" />
                    <h2 className="text-lg font-semibold text-green-600">Ticket Médio</h2>

                    <p className="mt-2 text-gray-600 text-xl font-semibold text-green-700">
                        {formatValue(ticketMedio)}
                    </p>

                    <span className="mt-1 text-sm text-gray-400">No período</span>
                </div>

                {/* Crescimento de pedidos */}
                <div
                    className="bg-white rounded-2xl shadow p-6 flex flex-col items-center text-center transition-shadow hover:shadow-lg"
                    aria-label="Crescimento no número de pedidos comparado ao último mês"
                >
                    <TrendingUp className="text-blue-500 mb-3" size={32} aria-hidden="true" />
                    <h2 className="text-lg font-semibold text-blue-600">
                        Crescimento no Nº de Pedidos
                    </h2>

                    {crescimentoPedidos != null ?
                        <p className="mt-4 text-2xl font-bold text-blue-600">
                            {(crescimentoPedidos >= 0 ? "+" : "") + crescimentoPedidos}%
                        </p> :
                        <>
                            <p className="mt-4 text-md font-bold text-blue-600">
                                Sem pedidos para o cálculo
                            </p>
                        </>
                    }

                    <span className="mt-1 text-sm text-gray-400">Comparado ao último mês</span>
                </div>
            </div>
        </div>
    );
}

export default Dashboard;
